/**
 * JourneyPage.tsx — the `/:clinicSlug` journey orchestrator (stage machine).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign);
 * the refresh/restore orchestrator below is the C1.8 navigation-resilience
 * slice (feat/patient-journey-navigation-resilience).
 *
 * WHY: one page owns the journey's stages —
 *   capture → projection → qualify → whatNext → questions → building
 *     → (result | stopped) → checkout
 * — and the shared state (canonical height/weight, leadId, assessmentId,
 * attribution, the qualification answer).
 *
 * `qualify` (the readiness mini form) and `whatNext` (the plain explainer) were
 * added on 2026-09-12. See the `Stage` declaration for why they sit after the
 * projection rather than after the capture form, and `leadReadiness.ts` for why
 * the mini form may ask exactly one of the three questions the CTO sketched.
 * Since 2026-09-25 (CTO) that question is REQUIRED and has no Continue: the
 * tap on an answer moves straight on to `whatNext` (QualificationStep.tsx).
 *
 * RouteChoice ("Two ways to begin") remains as a standalone
 * component for tests/a11y and non–Weight-Loss surfaces; the Weight Loss
 * JourneyPage never mounts it (Webflow already chose eligibility vs callback).
 * The `:clinicSlug` URL segment is NEVER
 * echoed into the page (an R28 hole); the page resolves the slug against the
 * clinic registry and renders the registry's vetted, category-level name.
 *
 * CLIN-02 (patient safety): the SUBMIT verdict is branched here. An
 * `eligibility === 'not_eligible'` result renders the StopScreen — NEVER a
 * positive "good news" screen. Only 'eligible' / 'clinician_review' reach the
 * positive ResultScreen and the plan/checkout funnel. The restore path below
 * honours the SAME rule from `GET …/progress` (`hardStop` OR a submitted/
 * rejected/approved `not_eligible` verdict → StopScreen, never a positive
 * screen).
 *
 * NO RE-ASKING: the patient already gave their email (Step-0 `CaptureStep` or
 * Webflow Lead) and date of birth (the `date`-kind question, via `QuestionFlow`'s
 * `onDobChange`) earlier in the journey. Both are kept here and threaded into
 * `CheckoutFlow` → `RegisterForm variant="checkout"`, which shows the email as a
 * read-only line and does not show the DOB at all (it is still SENT to identity)
 * rather than asking again.
 *
 * ── URL IDENTITY (navigation resilience) ────────────────────────────────────
 * Three params identify "where a visitor is", in this PRECEDENCE order
 * (a param further down never overrides one above it for the INITIAL stage
 * decision — each is captured once, at mount, into a ref/lazy-state so an
 * in-journey stage change or our own `?assessment=` stamping never re-triggers
 * the read):
 *
 *   1. `#resume=<assessmentId | grant>` (P1E) — the clinician plan-link
 *      handover email, and since 2026-09-23 also the unlock lanes' short-TTL
 *      GRANT (a NON-UUID fragment value, exchanged through
 *      `POST …/assessments/resume-exchange` for the id before the identical
 *      path runs — see the exchange effect). Skips Step-0 entirely and enters
 *      CHECKOUT for that assessment. Wins over BOTH `?assessment=` and
 *      `?lead=` — it is itself a later-stage, already-submitted entry point.
 *      In the FRAGMENT so neither the assessment id of a named patient nor a
 *      live grant ever reaches access logs / `Referer` / GTM `page_location`;
 *      the old `?resume=` query form is still read for links already in
 *      inboxes (always an assessment id, never a grant) and is scrubbed out
 *      of the URL on arrival (resumeHandoff.ts).
 *   2. `?assessment=<assessmentId>` — canonical Webflow eligibility handoff
 *      AND in-journey stamp. Widget creates Lead + Assessment then redirects
 *      here with `#lead=<leadId>` in the fragment (never the query string —
 *      leadId is a replayable bearer to H/W + email and must not hit access
 *      logs). `GET …/progress` with `atStart:true` → questions (H/W via lead
 *      resume + leading-capture auto-submit); mid-draft → questions; submitted
 *      → result/stop/checkout via the code gate.
 *   3. `?lead=<leadId>` — LEGACY projection-only handoff (Lead only, no
 *      `?assessment=`). Resume lead → Projection; Graph Continue creates the
 *      Assessment. New embeds use `#lead=` on the assessment redirect instead.
 *   (2026-09-25) A 3D SECURE RETURN — Stripe's `setup_intent` /
 *      `redirect_status` parameters plus `#checkout-return=<assessmentId>` (or
 *      the in-flow `?assessment=`) — ranks just below `#resume=` and above the
 *      rest: straight back into CHECKOUT for that assessment, no code gate, and
 *      CheckoutFlow finishes the order (`checkout/setupRedirectReturn.ts`).
 *
 * NEVER ANSWERS/PHI IN THE URL: only opaque ids (`assessment` in the query;
 * `#lead=` / `#resume=` in the fragment) and marketing tags
 * (`channel`/`campaign`/`v`) ever ride the address bar.
 *
 * RESTORE FAILURES (three distinct cases — never conflate them):
 *   A. Transient / network / 5xx on `GET …/progress` with `?assessment=`
 *      present → restoration ERROR state + Retry. The assessment id STAYS in
 *      the URL; no CaptureStep restart; no `createAssessment`. A temporary
 *      backend outage must not look like lost progress.
 *   B. Genuine unknown/expired/inaccessible (404) → fail soft to the normal
 *      CaptureStep start (safe invalid). `resumeExpired` stays reserved for
 *      the older, narrower resume contract.
 *   C. No `?assessment=` / `?lead=` / `#resume=` → intentional DIRECT ENTRY:
 *      CaptureStep. This is the safe fallback when someone opens `/weightloss`
 *      without a durable handoff — we do NOT invent Projection (no H/W) and we
 *      do NOT auto-create an Assessment. Capture creates a Lead on submit then
 *      enters Projection (still no RouteChoice on Continue). Canonical Weight
 *      Loss acquisition remains Webflow → widget → `?assessment=`.
 *
 * RESUME ENTRY (P1E): `#resume=<assessmentId>` — the link the clinician
 * plan-link handover emails a patient — skips Step-0 entirely and enters
 * CHECKOUT at plan selection for that already-submitted assessment (the
 * exact same hand-off point the normal post-submit "See my plan" uses). A
 * GUEST is minted an assessment-bound `checkoutToken` first (same grant as
 * "See my plan") so they can pick a plan, pay, THEN create an account if
 * they are not registered; a signed-in patient uses Bearer and skips
 * account. If mint or a later checkout call 404/409s (a bad/expired id, not
 * submitted), we land on a friendly "link expired" screen with a restart
 * into the normal journey — never a dead end.
 *
 * ⚠️ IT IS THE FRAGMENT, NOT A QUERY PARAM, and that is a security decision
 * rather than a style one. A query string lands verbatim in LB/WAF/nginx access
 * logs on first paint AND in GTM's `page_location` — and this id is a bearer
 * capability: it mints the checkout grant and, through the guest lane, reads the
 * clinician's medication recommendation. A fragment is never sent to the server
 * and never leaves the browser. Do not "tidy" it back to `?resume=`.
 *
 * WIDGET ENTRY: Webflow embed creates Lead (+ Assessment for eligibility),
 * redirects with opaque `?assessment=` + `#lead=` (preferred) or legacy `?lead=`
 * (projection-only). Portal enters at PROJECTION for both — graph first, then
 * questionnaires on Continue. Never Capture, never "Two ways to begin".
 * Callback completes inside the widget (thank-you); portal `/callback` remains
 * for direct/fallback entry only.
 *
 * LEAD HYDRATION (`POST …/leads/resume`) runs only when allowed:
 *   - legacy `?lead=` without `?assessment=` — on mount;
 *   - widget handoff (`?assessment=` + `#lead=`) — only after progress resolves
 *     to `draft` + `atStart:true`. Never on submitted assessments (the Tier-2
 *     code gate owns that data). The fragment is scrubbed from the address bar
 *     once read (router `navigate` replace — keeps React Router in sync).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import {
  createAssessment,
  exchangeResumeGrant,
  getAssessmentProgress,
  getAssessmentResultSummary,
  getClinic,
  getQuestions,
  mintCheckoutToken,
  recordLeadReadiness,
  recordPlanViewed,
  resumeLead,
  submitAnswer,
  submitAssessment,
} from '../api/journey';
import type {
  AssessmentProgress,
  AssessmentSubmitResult,
  ClinicPublicConfig,
  EvaluationResult,
} from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { trackEligibilityResult } from '../tracking/dataLayer';
import { emitGenerateLeadOnce, firstTouchOfResume } from '../tracking/generateLead';
import { UNTICKED, type MarketingChoices } from '../marketing/MarketingConsentFields';
import { postJourneyMarketingGrant } from '../marketing/journeyConsent';
import type { MarketingChannel } from '../api/types';
import { Button, ErrorState, Help, PageFrame, Title } from '../ui/kit';
import { BrandedLoader, useLoaderHold } from '../ui/BrandedLoader';
import { FadeIn } from '../ui/motion';
import { CheckoutFlow } from '../checkout/CheckoutFlow';
import { readCheckoutRedirectReturn, type CheckoutRedirectReturn, type SetupRedirectResult } from '../checkout/setupRedirectReturn';
import { clearCheckoutToken, readCheckoutToken, writeCheckoutToken } from '../checkout/checkoutTokenStorage';
import { parseAttribution, parseVariant } from './attribution';
import { CaptureStep, type CaptureResult } from './CaptureStep';
import { BuildingInterstitial, ProjectionInterstitial } from './ProjectionInterstitial';
import { BUILDING_BEAT_START, journeyLoaderCard, journeyLoaderMessage, runBuildingBeat, type BuildingBeatView } from './journeyLoaders';
import { QualificationStep } from './QualificationStep';
import { WhatHappensNext } from './WhatHappensNext';
import type { LeadReadinessKey } from './leadReadiness';
import { QuestionFlow, type AnsweredEntry, type HardStopRestartContext } from './QuestionFlow';
import { RestoreCodeEntry } from './RestoreCodeEntry';
// The portal's ONE BMI derivation — the same helper behind the ResultScreen dial.
import { bmiOf } from './projection';
import { ResultScreen } from './ResultScreen';
import { JourneyCallbackProvider } from './JourneyCallbackContext';
import { JourneyCallbackOffer } from './JourneyCallbackOffer';
import { StopScreen, notEligibleResult } from './StopScreen';
import { scrubHandoffIdsFromUrl } from './handoffScrub';
import { readLeadId, readLeadIdFromNavigationState } from './leadHandoff';
import { readResumeEntry, type ResumeEntry } from './resumeHandoff';
import { useResetViewOnChange } from '../ui/viewReset';
import { withBasename } from '../config';

/**
 * Merge chronological answer logs, one entry per questionKey, LAST WRITE WINS.
 *
 * The direction matters clinically. `handleAnswer` pushes unconditionally and the
 * in-flow Back button lets a patient re-answer an earlier question, so a
 * corrected answer appears LATER in the log than the one it replaces. Keeping
 * the FIRST occurrence replays the answer the patient explicitly changed — e.g.
 * someone who mis-taps "male", goes back and corrects to "female" (which is what
 * makes the pregnancy question visible), then hard-stops, gets replayed onto a
 * fresh assessment as "male" with the pregnancy answer still attached, and the
 * seeded local map hides the question so it is never re-asked. That assessment
 * then goes to submit.
 *
 * `Map.set` updates in place, so the correction wins while the original
 * chronological position is preserved.
 */
function mergeAnswerLogs(base: AnsweredEntry[], extra: AnsweredEntry[]): AnsweredEntry[] {
  const byKey = new Map<string, AnsweredEntry>();
  for (const entry of [...base, ...extra]) byKey.set(entry.questionKey, entry);
  return [...byKey.values()];
}

/**
 * ⚠️ `qualify` AND `whatNext` SIT AFTER `projection`, NOT AFTER `capture`
 * (2026-09-12). The CTO's sketch put the qualification form straight after the
 * first capture form. It is one screen later for a reason that is about
 * coverage rather than compliance: Step 0 is not on the canonical acquisition
 * path. Most real leads arrive from the Webflow widget, which captures height,
 * weight and email on its own page and hands the journey over at `projection`
 * (`?assessment=` + `#lead=`, or legacy `?lead=`). A screen between `capture`
 * and `projection` would never be shown to them. Every lane — portal capture
 * and widget hand-off alike — converges on `projection → questions`, so that is
 * where a screen the whole funnel must see has to go.
 */
type Stage =
  | 'capture'
  | 'projection'
  | 'qualify'
  | 'whatNext'
  | 'questions'
  | 'building'
  | 'result'
  | 'stopped'
  | 'checkout';

/** A resolved `AssessmentSubmitResult`, tagged so callers know whether it came
 * from a real submit or was synthesised from the progress read (restore). */
interface StageDecision {
  stage: Stage;
  result?: AssessmentSubmitResult;
  initialQuestionKey?: string | null;
}

/**
 * Map the STRICT, PHI-free `GET …/progress` response to a journey stage —
 * the ONLY place restore logic lives, so `checkoutFast`/`useAuth` decisions
 * stay easy to audit against CLIN-02. `signedIn` decides whether an
 * already-submitted, eligible-ish assessment restores straight into
 * CHECKOUT (an authed patient already registered — CheckoutFlow itself opens
 * at 'plan') or the guest ResultScreen (not yet registered — "See my plan"
 * is the existing, unchanged hand-off into checkout's account step).
 */
function mapProgressToStage(progress: AssessmentProgress): StageDecision {
  if (progress.state === 'draft') {
    // Resume the questionnaire. Tier-1 no longer hands back H/W (no leadId) or
    // the exact next-question key (answer-derived, #57). Mid-flow drafts and
    // cold `?assessment=` (no `#lead=`) re-enter QUESTIONS — the leading capture
    // questions re-collect H/W when needed. Widget handoff (`?assessment=` +
    // `#lead=` atStart) and legacy `?lead=` open Projection via their own
    // effects / restore branch (graph first, then questionnaires).
    return { stage: 'questions' };
  }

  // submitted (approved / rejected collapse to `submitted` server-side). Tier-1
  // carries NO verdict, so we cannot decide result vs stopped vs checkout here —
  // route to the code gate. The authoritative eligibility comes back on the
  // Tier-2 result-summary, which `hydrateResultFromToken` turns into the final
  // stage (stopped for not_eligible; checkout for a signed-in / ?checkout=1
  // eligible patient; the guest ResultScreen otherwise). CLIN-02: the verdict is
  // ALWAYS checked before checkout, and never re-dressed as good news.
  return { stage: 'result' };
}

export function JourneyPage() {
  const { clinicSlug = 'weightloss' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isSignedIn, status: authStatus } = useAuth();
  // The Result screen's greeting (premium pass, 2026-09-24): ONLY the first name
  // the patient typed on Step 0 of THIS journey, held in memory. Nothing else
  // feeds it — not the widget hand-off (the lead read deliberately carries no
  // name since 2026-08-28) and not a signed-in session's `fullName`, which is a
  // full name, and splitting one to guess a first name is deriving a value the
  // journey was never given. No name ⇒ the greeting simply has none.
  const [journeyFirstName, setJourneyFirstName] = useState<string | null>(null);
  const attribution = parseAttribution(location.search);
  const variant = parseVariant(location.search);
  // The admin handover email's re-entry point (P1E). Fragment first
  // (`#resume=`), with the deprecated `?resume=` query form still honoured for
  // links already in inboxes — see resumeHandoff.ts for why the id must not be
  // in the query string. Captured ONCE into lazy state (the same freeze as
  // `assessmentIdFromUrl`) because the scrub effect below rewrites
  // `location.search`: re-reading it each render would drop the entry
  // mid-journey and take the expired-link fallback (`onAssessmentGone`,
  // wired at the CheckoutFlow below) with it.
  //
  // TWO KINDS OF VALUE, ONE LANE (pinned addendum, 2026-09-23). A UUID is the
  // assessment id and keeps today's path byte-identical. A NON-UUID fragment
  // value is a short-TTL GRANT (the unlock lanes mint
  // `/weightloss#resume=<grant>` so the durable id never rides a WhatsApp
  // thread) — it is exchanged below, and from the moment the exchange answers,
  // `resumeId` IS the returned assessment id: every downstream effect (the
  // checkout-grant mint, the plan-viewed beacon, the expired fallback, the
  // drop-off offer) reads it exactly as if the id had been in the fragment.
  const [resumeEntry] = useState<ResumeEntry | null>(() => readResumeEntry(location));
  const resumeGrant = resumeEntry?.grant ? resumeEntry.value : undefined;
  // Set once the grant exchange answers; stays undefined on the UUID lane.
  const [exchangedResumeId, setExchangedResumeId] = useState<string | undefined>(undefined);
  const resumeId = resumeEntry ? (resumeEntry.grant ? exchangedResumeId : resumeEntry.value) : undefined;
  // BACK FROM THE BANK'S 3D SECURE PAGE (2026-09-25). A card check that needs a
  // full-page redirect sends the patient back here with Stripe's
  // `setup_intent` / `redirect_status` parameters, and `#checkout-return=<id>`
  // naming the checkout (or, from a build that predates the fragment, the
  // in-flow `?assessment=`) — see `checkout/setupRedirectReturn.ts`. That is a
  // patient who has just confirmed their card for an order still waiting on its
  // hold, so the page goes STRAIGHT back into checkout for that assessment, like
  // the plan link and unlike a cold `?assessment=` restore: no emailed-code gate
  // between a patient and the order they are in the middle of paying for. The
  // grant is ALWAYS a fresh public mint (the plan link's; never the token this
  // tab stored, which may be a 15-minute re-entry token that died at the bank —
  // see the mint effect) — the same capability `#resume=<id>` has always had,
  // and nothing more:
  // CheckoutFlow shows only what orders holds, and attaches only a card whose
  // SetupIntent orders names for this case. Frozen at mount, BEFORE the scrub
  // below takes every return key off the address bar (a reload never replays
  // it). A `#resume=` entry wins; the two never meet on a URL this portal writes.
  const [paymentReturn] = useState<CheckoutRedirectReturn | null>(() =>
    resumeEntry ? null : readCheckoutRedirectReturn(location),
  );
  // The Stripe half, handed to CheckoutFlow ONCE: cleared when it reports the
  // return settled, so a later remount of the checkout cannot act on it again.
  const [pendingPaymentReturn, setPendingPaymentReturn] = useState<SetupRedirectResult | null>(
    () => paymentReturn?.setup ?? null,
  );
  const settlePaymentReturn = useCallback(() => setPendingPaymentReturn(null), []);
  // The assessment a checkout-ENTRY link opened (the plan link's `#resume=`, or
  // a 3D Secure return): the one the guest checkout grant below is for.
  const checkoutEntryId = resumeId ?? paymentReturn?.assessmentId;
  // `?assessment=<uuid>` — the navigation-resilience URL identity. Captured
  // ONCE via lazy state so our own `navigate(..., {replace:true})` stamping
  // (which rewrites `location.search` to ADD this very param) never re-reads
  // itself and re-triggers the restore effect. `?resume=` wins when both are
  // somehow present (it is itself a later-stage, already-submitted entry).
  const [assessmentIdFromUrl] = useState<string | undefined>(() => {
    // A 3D Secure return names its assessment itself and must not take the
    // cold-restore road (the emailed-code gate) that `?assessment=` opens.
    if (resumeEntry || paymentReturn) return undefined;
    const raw = new URLSearchParams(location.search).get('assessment')?.trim();
    return raw || undefined;
  });
  // Opaque stage flag (NOT PHI): stamped when the guest clicks "See my plan"
  // so a hard refresh restores Create-account instead of ResultScreen.
  const [preferCheckoutFromUrl] = useState(
    () => new URLSearchParams(location.search).get('checkout') === '1',
  );
  // The Webflow-widget hand-off (W1) — opaque leadId from `#lead=` (canonical)
  // or legacy `?lead=` query fallback (kept for cached embeds — remove once the
  // embed `?v=` is bumped and the Webflow snippet is updated). Read once at
  // mount (same freeze as `assessmentIdFromUrl`). `?resume=` wins when present.
  const [widgetLeadId] = useState<string | undefined>(() => {
    if (resumeEntry || paymentReturn) return undefined;
    // ONE read for both URL forms (fragment wins), shared with the scrub below
    // so the two can never disagree about what counts as a hand-off id.
    //
    // …and then history STATE, which is how the callback lane's Back hands the
    // id over. That lane scrubs the id off its own URL, so by the time it
    // replaces onto the journey there is nothing on the address bar left to
    // read — and putting it back there would undo the scrub one button press
    // later. Without this, a visitor who opens the callback form from the widget
    // and then backs out lands on Step 0 and retypes the height and weight the
    // widget already collected. See `readLeadIdFromNavigationState`.
    return readLeadId(location) ?? readLeadIdFromNavigationState(location.state) ?? undefined;
  });

  const [clinic, setClinic] = useState<ClinicPublicConfig | null>(null);
  const [clinicState, setClinicState] = useState<'loading' | 'ready' | 'missing' | 'error'>('loading');
  // DIRECT ENTRY (`/:clinicSlug` with no resume/assessment/lead): intentional
  // fallback is Capture — NOT Projection. There is no durable H/W/assessment yet;
  // inventing Graph would be fragile. Capture lets the visitor start eligibility
  // in-portal (creates Lead on submit → Projection). Canonical Weight Loss
  // acquisition is Webflow widget → `?assessment=` → Projection; bare /weightloss
  // is the deliberate recovery/direct-entry path, not a silent Graph.
  const [stage, setStage] = useState<Stage>(resumeEntry || paymentReturn ? 'checkout' : 'capture');
  /**
   * EVERY JOURNEY STAGE CHANGE LANDS AT THE TOP OF THE NEW SCREEN (2026-08-20).
   * The same defect the CTO reported on the post-payment screen lives here too,
   * and for the same structural reason: this page swaps whole screens without
   * changing the URL, so nothing resets the scroll. A long questionnaire screen
   * followed by a short result, or the result followed by checkout, left the
   * window somewhere below everything the new screen contains. Fixing only the
   * screen that was reported would leave the same bug in the four transitions
   * before it. Focus moves with the scroll — see ui/viewReset.ts for why an
   * announcement alone would not be enough.
   */
  useResetViewOnChange(stage);
  const [heightCm, setHeightCm] = useState(0);
  const [weightKg, setWeightKg] = useState(0);
  const [leadId, setLeadId] = useState<string | undefined>();
  // Set once this journey has sent a call-back request from the in-journey
  // offer. Held HERE, not in the offer, because the offer remounts as the
  // stages change — and a patient who has asked must not be offered it again
  // on the next screen (a second request would mint a second ticket).
  const [callbackRequested, setCallbackRequested] = useState(false);
  // True while Step 0's capture loader ("Setting up your health check") is on
  // screen (2026-09-25). The call-back offer under the capture stage is held
  // back meanwhile — the loader stands alone, like the checking loader, and the
  // stage has not changed, so nothing else would take the offer away.
  const [captureLoaderShown, setCaptureLoaderShown] = useState(false);
  /**
   * The "Keeping in touch" marketing ticks from the questionnaire's consent
   * screen (2026-09-23, one-flow move). BROWSER STATE on purpose: the server
   * refuses the marketing-consent write until the assessment is SUBMITTED with
   * a positive verdict, so the tick is held here — outliving QuestionFlow
   * remounts (a hard-stop "Back" re-mints the assessment) — and recorded by
   * `runSubmit` the moment the submit succeeds. UNTICKED default (PECR reg. 22),
   * and nothing anywhere reads it to gate anything.
   */
  const [marketingTicks, setMarketingTicks] = useState<MarketingChoices>(UNTICKED);
  const toggleMarketingTick = useCallback((channel: MarketingChannel) => {
    setMarketingTicks((current) => ({ ...current, [channel]: !current[channel] }));
  }, []);
  /**
   * Whether the consent screen's WhatsApp box was ON SCREEN — reported by the
   * "Keeping in touch" block itself (`false` on mount, `true` only once the box
   * has rendered, which it does only when the server's offer check says the
   * lead holds a UK mobile). `runSubmit` hands it to the grant so a WhatsApp
   * tick is recorded only for a box the patient was shown.
   *
   * A REF, not state: nothing renders from it, and it is read once, after the
   * submit resolves. It is NOT reset when the block unmounts — that happens the
   * instant the patient presses Continue, before the grant is built — only by
   * the next mount's own report and by `restart`.
   */
  const marketingWhatsappShownRef = useRef(false);
  const reportMarketingWhatsappShown = useCallback((shown: boolean) => {
    marketingWhatsappShownRef.current = shown;
  }, []);
  const [journeyEmail, setJourneyEmail] = useState<string | undefined>();
  const [journeyDob, setJourneyDob] = useState<string | undefined>();
  // A GRANT entry starts with NO assessment id at all — the exchange effect
  // below supplies it, which is what keeps every consumer of `assessmentId`
  // ignorant of how the id arrived.
  const [assessmentId, setAssessmentId] = useState<string | undefined>(
    (resumeEntry && !resumeEntry.grant ? resumeEntry.value : undefined) ??
      paymentReturn?.assessmentId ??
      assessmentIdFromUrl,
  );
  const [initialQuestionKey, setInitialQuestionKey] = useState<string | null | undefined>(undefined);
  // Chronological interactive answers carried across hard-stop remounts so a
  // second consecutive stop still has every pre-stop answer to replay (the
  // remounted QuestionFlow's own answerLogRef would otherwise start empty).
  const replayLogRef = useRef<AnsweredEntry[]>([]);
  const [seededAnswers, setSeededAnswers] = useState<AnsweredEntry[] | undefined>(undefined);
  // The full interactive answer log from the just-completed questionnaire —
  // captured off QuestionFlow's onComplete so the post-submit CLIN-02
  // not_eligible StopScreen can replay back to the last answered question
  // (see backToLastAnsweredQuestion below) instead of only Projection.
  const submittedAnswerLogRef = useRef<AnsweredEntry[]>([]);
  // True when JourneyPage already posted leading capture answers during a
  // hard-stop resume — QuestionFlow must not auto-submit them again.
  const [leadingCaptureSubmitted, setLeadingCaptureSubmitted] = useState(false);
  // In-flight mint+replay after an in-flow hard-stop "Back" — keeps a branded
  // loader up for the serial round-trips (never a blank screen).
  const [hardStopResuming, setHardStopResuming] = useState(false);
  // Mid-replay hard_stop verdict — StopScreen owned here because QuestionFlow
  // is not mounted until replay finishes (or this fires).
  const [replayHardStop, setReplayHardStop] = useState<{
    result: EvaluationResult;
    restart?: HardStopRestartContext;
  } | null>(null);
  const [submitResult, setSubmitResult] = useState<AssessmentSubmitResult | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  // Set when the resumed assessment turns out to be gone/ineligible (404/409)
  // — renders the friendly "link expired" fallback into the normal journey.
  const [resumeExpired, setResumeExpired] = useState(false);
  // True while the widget lead (`?lead=`) is being resolved — the page holds on
  // the spinner so the visitor never flashes Step 0 before the projection.
  const [leadPending, setLeadPending] = useState(Boolean(widgetLeadId));
  // True while `?assessment=` is being resolved via GET …/progress — the page
  // holds on the SAME branded spinner so a refresh never flashes capture (or
  // any other stage) before the restore decision lands.
  const [restoring, setRestoring] = useState(Boolean(assessmentIdFromUrl));
  // Projection Back → Capture ONLY when the visitor typed Step-0 in this SPA
  // session. Widget / `?assessment=` / `?lead=` handoffs use history.back()
  // so Back does not reintroduce Capture or RouteChoice.
  const [projectionBackToCapture, setProjectionBackToCapture] = useState(false);
  // The RAW progress read result, decoupled from the auth-dependent stage
  // decision below (see the two-effect split just under this). `undefined`
  // = still in flight (or not started), `null` = genuine unknown/404
  // (case B — fail soft to capture), otherwise the resolved payload.
  const [restoredProgress, setRestoredProgress] = useState<AssessmentProgress | null | undefined>(undefined);
  // Case A — transient/network/5xx on the progress read. Distinct from the
  // 404 fail-soft above: keep `?assessment=` + in-memory assessmentId, show
  // a retryable error, and NEVER drop the patient onto CaptureStep.
  const [restoreFailed, setRestoreFailed] = useState(false);
  // Option-C re-entry gate. A RETURN visit (cold `?assessment=`, no journey
  // state) that resolves to the ResultScreen needs the emailed code → token
  // before the sensitive H/W hydrate. Non-null → render RestoreCodeEntry; the
  // token it mints is consumed immediately by `hydrateResultFromToken` and
  // never parked in state. In-flow results never set this (they come through
  // runSubmit, which re-sources H/W from the lead — no code).
  const [restoreCodePurpose, setRestoreCodePurpose] = useState<null | 'result'>(null);
  // The re-entry token minted by a return-visit code gate, kept in memory (never
  // the URL) so a single verification covers every Tier-2 read this session —
  // the ResultScreen hydrate here AND the downstream checkout account-bootstrap
  // + register bind (threaded to CheckoutFlow). undefined in-flow (no code was
  // needed to reach the result), so checkout mints its own at account creation.
  const [reEntryToken, setReEntryToken] = useState<string | undefined>();
  // Read inside the restore decision effect WITHOUT making it a dependency —
  // otherwise minting the token would re-fire the effect and double-hydrate.
  const reEntryTokenRef = useRef(reEntryToken);
  reEntryTokenRef.current = reEntryToken;
  // Read the settled auth state inside the token-hydrate callback WITHOUT making
  // it a dependency (isSignedIn hydrates async; a dep would re-create the stable
  // callback and re-fire the restore effect).
  const signedInRef = useRef(isSignedIn);
  signedInRef.current = isSignedIn;

  // Scrub BOTH hand-off ids off the address bar the moment they have been
  // captured above — `resume=` (assessment id) and `lead=` (a bearer capability
  // that dereferences to a named patient's H/W + email), in either the query or
  // the fragment. This effect runs on the FIRST commit, and a child's effect
  // runs before its parent's, so it lands before AppShell's `loadGtm()` — the
  // container never sees either id in `page_location`. It cannot un-log the
  // request that carried a deprecated `?resume=`/`?lead=` here (see the module
  // headers); it stops the id travelling any further.
  //
  // ONE CALL, because there is one address bar. Two scrubs run back to back are
  // both computed from the SAME `location` snapshot — navigating inside an
  // effect does not refresh `useLocation()` before the next statement runs — so
  // the second `replace` writes back the key the first has just removed, and a
  // link carrying BOTH (`/weightloss?resume=…&lead=…`, a real shape) ended up
  // with `?resume=` restored. That ordering is not a convention here any more:
  // `scrubHandoffIdsFromUrl` owns both keys, computes the finished URL and
  // rewrites the address bar exactly once, so there is no second call to get
  // wrong and no intermediate URL for GTM to read. See handoffScrub.ts.
  useEffect(() => {
    scrubHandoffIdsFromUrl(navigate, location);
  }, [navigate, location]);

  // W1 widget hand-off: resolve the lead into H/W (+ email) for capturePrefill
  // and open Projection (graph first — Continue then enters questionnaires).
  // Legacy `?lead=` only → Projection (assessment minted on Graph Continue).
  // Canonical `?assessment=` + `#lead=` → hydrate only after progress confirms
  // draft + atStart (widget handoff); never on submitted (code gate) restores.
  useEffect(() => {
    if (!widgetLeadId) return;

    if (assessmentIdFromUrl) {
      if (restoredProgress === undefined) return;
      if (restoredProgress === null) {
        setLeadPending(false);
        return;
      }
      if (!(restoredProgress.state === 'draft' && restoredProgress.atStart)) {
        setLeadPending(false);
        return;
      }
    }

    let cancelled = false;
    resumeLead(widgetLeadId)
      .then((lead) => {
        if (cancelled) return;
        setHeightCm(lead.heightCm);
        setWeightKg(lead.weightKg);
        setLeadId(lead.leadId);
        setJourneyEmail(lead.email);
        setProjectionBackToCapture(false);
        // Funnel C1 — generate_lead for the lane ~97% of real patients actually
        // arrive down. The WIDGET created this Lead (`embed/main.ts`), so
        // `CaptureStep`'s C1 — the only one that existed — never ran for them,
        // while `questionnaire_start` and everything after it DID fire off the
        // leadId in this hand-off. That is the reported empty-step-1 funnel.
        //
        // Fired HERE, on a lead that has actually resolved, rather than on the
        // mere presence of an id in the URL: pushing a lead_id that dereferences
        // to nothing would put rows in the funnel that can never join to a Deal.
        // The cost is that a hand-off whose `resumeLead` 404s is not counted —
        // an UNDERCOUNT, which is the safe direction, and the same reason this
        // does not fire on a mid-draft or submitted re-open (the guard above
        // only resumes a draft `atStart`): that visitor was counted on their
        // first arrival and is not a new lead.
        //
        // `emitGenerateLeadOnce` is the ONLY way this event is pushed anywhere
        // in the app — CaptureStep's direct-entry lane goes through the same
        // door — so the two lanes cannot both count one lead. See its header
        // for how once-only survives a refresh, a re-mount, StrictMode and a
        // patient re-opening the same link.
        //
        // THE SERVER IS THE AUTHORITY on "have we counted this one already",
        // because this lane cannot know: a second tab, a forwarded link and a
        // bfcache Back onto the Webflow page followed by another press of the
        // CTA (the widget re-uses its cached `sessionLead`, so the SAME leadId
        // comes back) are all fresh documents. clinic-service stamps
        // `Lead.resumeConsumedAt` on the first spend of the hand-off and reports
        // it as `firstResume`; the portal keeps NOTHING on the visitor's device
        // to answer this, which is a PECR reg. 6 line, not a preference.
        emitGenerateLeadOnce(lead.leadId, firstTouchOfResume(lead));
        // Graph first for both widget paths (legacy `?lead=` and `?assessment=` +
        // `#lead=`). Capture already ran in the Webflow embed.
        setStage('projection');
        setLeadPending(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLeadPending(false);
      });
    return () => {
      cancelled = true;
    };
  }, [widgetLeadId, assessmentIdFromUrl, restoredProgress]);

  // Navigation-resilience restore: resolve `?assessment=` via GET …/progress.
  // Split into TWO effects on purpose:
  //   1. below — fires the read (keyed by `restoreNonce` so Retry can re-run
  //      it) and stores the RAW result, or classifies the failure as
  //      unknown-404 vs transient;
  //   2. further down — maps a SUCCESSFUL result to a stage, but only once
  //      `authStatus` has also settled.
  // WHY split: `isSignedIn` hydrates ASYNCHRONOUSLY (AuthProvider's own
  // GET /auth/me, fired from its own effect) and the two reads race — a fast
  // progress response can resolve before auth has hydrated. Deciding the
  // checkout-vs-result branch (mapProgressToStage's `signedIn` argument)
  // needs the REAL, settled auth state, not a mount-time guess. Never
  // renders the wrong branch: the branded loader (see `restoring` below)
  // simply stays up a little longer while auth catches up.
  // `restoreNonce` bumps on Retry so the effect re-fires without clearing the
  // URL identity. A local `cancelled` flag (not a ref) correctly handles
  // React 18 StrictMode's dev-only double invoke.
  const [restoreNonce, setRestoreNonce] = useState(0);
  const runRestore = useCallback(() => {
    setRestoreNonce((n) => n + 1);
  }, []);

  // Option C: a verified re-entry token in hand → do the Tier-2 H/W+BMI
  // hydrate and paint ResultScreen. Called by RestoreCodeEntry.onVerified on a
  // return visit (see restoreCodePurpose). The token rides in the POST body;
  // the id never leaves the server. A DEAD token (404: expired/used — normal at a
  // 15-min TTL) drops back to the code gate for a fresh code; a TRANSIENT fault
  // keeps `?assessment=` and offers Retry, never a Capture restart.
  const hydrateResultFromToken = useCallback((token: string) => {
    setRestoreCodePurpose(null);
    setReEntryToken(token); // reused for the downstream checkout reads (non-consuming)
    setRestoring(true);
    getAssessmentResultSummary(token)
      .then((summary) => {
        setHeightCm(summary.heightCm);
        setWeightKg(summary.weightKg);
        setSubmitResult({
          // The summary never echoes the id (clinic #57); use the one we already
          // hold from the `?assessment=` URL.
          assessmentId: assessmentIdFromUrl ?? '',
          state: summary.state,
          eligibility: summary.eligibility ?? 'clinician_review',
          flags: summary.flags,
        });
        // CLIN-02: the Tier-2 summary carries the AUTHORITATIVE verdict. Tier-1
        // progress no longer reveals eligibility (#57), so this is the one place
        // the cold-restore outcome is decided. not_eligible → StopScreen, never
        // the positive "good news" result. An eligible patient who is signed in
        // (already registered) or arrived via `?checkout=1` ("See my plan") goes
        // straight to checkout; a guest lands on the ResultScreen.
        const eligible = summary.eligibility !== 'not_eligible';
        setStage(!eligible ? 'stopped' : signedInRef.current || preferCheckoutFromUrl ? 'checkout' : 'result');
        setRestoring(false);
        setRestoreFailed(false);
      })
      .catch((err: unknown) => {
        setRestoring(false);
        // A 404 means the token is expired / used / unknown — it is DEAD, so a
        // Retry that re-posts it would dead-loop forever (a real event at a 15-min
        // TTL, #120). Clear it and re-open the code gate for a fresh code instead.
        // A transient fault (5xx / network) keeps the still-valid token for Retry.
        if (err instanceof ApiError && err.status === 404) {
          setReEntryToken(undefined);
          setRestoreFailed(false);
          setRestoreCodePurpose('result');
          return;
        }
        setRestoreFailed(true);
      });
  }, [preferCheckoutFromUrl, assessmentIdFromUrl]);

  useEffect(() => {
    if (!assessmentIdFromUrl) return;
    let cancelled = false;
    setRestoreFailed(false);
    setRestoring(true);
    setRestoredProgress(undefined);
    getAssessmentProgress(assessmentIdFromUrl, clinicSlug)
      .then((progress) => {
        if (!cancelled) setRestoredProgress(progress);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Case B — genuine unknown/expired/inaccessible. Same non-enumerating
        // 404 the clinic-service progress endpoint returns for purged / wrong
        // clinic / bound-to-other. Fail soft to CaptureStep (never invent a
        // stage from the caught error).
        if (err instanceof ApiError && err.status === 404) {
          setRestoredProgress(null);
          return;
        }
        // Case A — transient/network/5xx. Keep assessment identity; surface
        // a retryable restoration error. Do NOT fall through to CaptureStep.
        setRestoreFailed(true);
        setRestoring(false);
        setLeadPending(false);
      });
    return () => {
      cancelled = true;
    };
  }, [assessmentIdFromUrl, clinicSlug, restoreNonce]);

  useEffect(() => {
    if (restoredProgress === undefined) return; // the progress read is still in flight.
    if (restoredProgress === null) {
      // Case B — 404 unknown/purged/inaccessible. Fail soft to capture; clear
      // the in-memory assessmentId so a subsequent startForm creates a fresh
      // draft. The stale `?assessment=` query param is overwritten on stamp.
      setStage('capture');
      setAssessmentId(undefined);
      setRestoring(false);
      setRestoreFailed(false);
      return;
    }
    if (authStatus === 'loading') return; // wait for the REAL signed-in state too.
    const decision = mapProgressToStage(restoredProgress);
    setInitialQuestionKey(null);

    // A `submitted` restore lands on the code gate: the verdict + H/W+BMI are
    // Tier-2, so gate them behind the emailed code → token (Option C). This is a
    // cold `?assessment=` restore only; an in-flow result comes through runSubmit
    // (no code). Once the code is verified, `hydrateResultFromToken` reads the
    // authoritative eligibility and routes to stopped / result / checkout. If a
    // token was already verified this session (Retry after a transient failure)
    // reuse it — never a second code prompt.
    if (decision.stage === 'result' && assessmentIdFromUrl) {
      if (reEntryTokenRef.current) {
        hydrateResultFromToken(reEntryTokenRef.current);
      } else {
        setRestoreCodePurpose('result');
        setRestoring(false);
        setRestoreFailed(false);
      }
      return;
    }

    // Widget eligibility handoff (`?assessment=` + `#lead=` at start): land on
    // Projection while leadPending keeps the loader up until H/W hydrates —
    // never flash questionnaires before the graph.
    if (
      decision.stage === 'questions' &&
      widgetLeadId &&
      restoredProgress.atStart
    ) {
      setStage('projection');
    } else {
      setStage(decision.stage);
    }
    setRestoring(false);
    setRestoreFailed(false);
  }, [
    restoredProgress,
    authStatus,
    assessmentIdFromUrl,
    clinicSlug,
    hydrateResultFromToken,
    widgetLeadId,
  ]);

  const loadClinic = useCallback(() => {
    setClinicState('loading');
    let cancelled = false;
    getClinic(clinicSlug)
      .then((config) => {
        if (cancelled) return;
        setClinic(config);
        setClinicState('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // A genuine 404 is a neutral not-found; anything else is retryable.
        setClinicState(err instanceof ApiError && err.status === 404 ? 'missing' : 'error');
      });
    return () => {
      cancelled = true;
    };
  }, [clinicSlug]);

  useEffect(() => loadClinic(), [loadClinic]);

  /** Stamp `?assessment=<id>` into the URL via REPLACE — every other param
   * (lead/attribution/variant) is preserved verbatim; never a PUSH (a
   * refresh/back must not re-run `startForm`). */
  const stampAssessment = useCallback(
    (id: string) => {
      const params = new URLSearchParams(location.search);
      params.set('assessment', id);
      const search = params.toString();
      navigate(`/${clinicSlug}${search ? `?${search}` : ''}`, { replace: true });
    },
    [location.search, navigate, clinicSlug],
  );

  /** Stamp opaque `checkout=1` so hard refresh restores Create-account (not Result). */
  const stampCheckout = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.set('checkout', '1');
    const search = params.toString();
    navigate(`/${clinicSlug}${search ? `?${search}` : ''}`, { replace: true });
  }, [location.search, navigate, clinicSlug]);

  const [checkoutGrant, setCheckoutGrant] = useState<string | undefined>();
  const [checkoutEnterError, setCheckoutEnterError] = useState<string | null>(null);
  // Nothing about a weight photo is derived here, and nothing should be. This
  // page used to compute whether the plan step must mandate one, then read the
  // server's `weightPhotoRequired` over the top of it; the plan step asks for no
  // photo at all since 2026-09-04 (see `checkout/PlanSelection`'s header), so
  // both the derivation and the read are gone rather than left dormant.
  // Guest `?resume=` must mint the checkout grant BEFORE CheckoutFlow mounts
  // — without it, guest Orders / plan-options / recs all 401 and the
  // clinician-sent link looks broken. Signed-in skips this (Bearer).
  const [resumeGrantPending, setResumeGrantPending] = useState(() => Boolean(resumeEntry || paymentReturn));
  const [resumeGrantNonce, setResumeGrantNonce] = useState(0);
  // Bumped by "Try again" when the GRANT EXCHANGE itself failed transiently —
  // a separate nonce from `resumeGrantNonce` because the two retries replay
  // different requests (the exchange vs the checkout-token mint).
  const [resumeExchangeNonce, setResumeExchangeNonce] = useState(0);

  // THE GRANT EXCHANGE (pinned addendum, 2026-09-23). A non-UUID `#resume=`
  // value is a short-TTL grant, not an assessment id — trade it for the id
  // FIRST, then let the whole existing plan-link path run on the answer
  // untouched: setting `exchangedResumeId` + `assessmentId` is the ONLY thing
  // this effect does on success, so the checkout-grant mint below, the
  // plan-viewed beacon, the expired fallback and the drop-off offer all fire
  // exactly as they do when the id itself arrives in the fragment.
  //
  // A 404 is the SAME "This link has expired" screen as a dead assessment id
  // (the server answers every unknown/expired/forged token with one generic
  // 404, and this page must not become the oracle the server refuses to be).
  // Anything else is a transient fault: the ordinary retryable error, whose
  // retry re-runs THIS exchange (see the ErrorState handler below).
  useEffect(() => {
    if (!resumeGrant || exchangedResumeId) return;
    let cancelled = false;
    setResumeGrantPending(true);
    exchangeResumeGrant(resumeGrant).then(
      (exchange) => {
        if (cancelled) return;
        setExchangedResumeId(exchange.assessmentId);
        setAssessmentId(exchange.assessmentId);
        // NOT setResumeGrantPending(false): the plan-link grant mint effect
        // below owns that flag from here, exactly as on the UUID lane.
      },
      (err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setResumeExpired(true);
        } else {
          setCheckoutEnterError('Sorry — we could not start checkout. Please try again.');
        }
        setResumeGrantPending(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [resumeGrant, exchangedResumeId, resumeExchangeNonce]);

  const enterCheckout = useCallback(async () => {
    if (!assessmentId) return;
    stampCheckout();
    setCheckoutEnterError(null);
    // Reuse an OTP-verified restore token when we have one (return visit already
    // proved email). Otherwise mint/reuse the public checkout grant — that grant
    // authorises guest Orders only and must not skip Create-account OTP.
    let token = reEntryTokenRef.current ?? readCheckoutToken(assessmentId) ?? checkoutGrant;
    if (!token) {
      try {
        const minted = await mintCheckoutToken(assessmentId, clinicSlug);
        token = minted.checkoutToken;
        /* Only when the server answered. A reused grant skips this branch
           entirely and leaves the verdict undefined, which the derivation below
           reads as "fall back to the client-side one". */
      } catch {
        setCheckoutEnterError('Sorry — we could not start checkout. Please try again.');
        return;
      }
    }
    writeCheckoutToken(assessmentId, token);
    setCheckoutGrant(token);
    setStage('checkout');
  }, [assessmentId, clinicSlug, stampCheckout, checkoutGrant]);

  // Clinician plan-link (`?resume=`): mint the guest checkout grant so the
  // patient can select a plan and pay before creating an account. A 404/409
  // here is the same "link expired" as a later checkout 409 (TA-06). Do NOT
  // require this grant for `?checkout=1` restore — that path already holds
  // an OTP-verified reEntryToken from the code gate.
  //
  // THE 3D SECURE RETURN RIDES THIS SAME MINT (2026-09-25, `checkoutEntryId`),
  // and ALWAYS MINTS FRESH. Only its failure differs — a return is not a link
  // that can have EXPIRED, so a 404 or 409 is the ordinary retryable error,
  // never the plan link's expired screen.
  useEffect(() => {
    if (!checkoutEntryId || !assessmentId) return;
    if (authStatus === 'loading') return;
    if (isSignedIn) {
      setResumeGrantPending(false);
      return;
    }
    // Reuse a grant we already hold — but ONLY on the first pass. A retry
    // (`resumeGrantNonce`) means the stored grant did not work: it has expired,
    // been consumed by a register bind, or belongs to a stale session. Taking
    // the sessionStorage copy again on every attempt made the retry a no-op and
    // the clinician's plan-link permanently dead for that browser, because the
    // only way out was clearing site data. On a retry, drop it and mint fresh.
    //
    // NEVER on a 3D Secure return (release review, 2026-09-25). The token this
    // tab stored may be the emailed-code restore's re-entry token, which lives
    // 15 minutes (clinic `REENTRY_TTL_MS`) — `enterCheckout` stores it as the
    // checkout token — while the public grant lives 30 days. A bank step that
    // ends past those 15 minutes then came back to a dead token: the guest
    // resume was refused, CheckoutFlow's "Try again" re-sent the SAME token,
    // and the Stripe keys were already scrubbed, so nothing could retry the
    // return — the patient confirmed at the bank was lost exactly as before
    // this fix. A fresh public mint is the same capability (keyed by the
    // assessment id alone), one request, and the account step mints its own
    // code anyway.
    const existing = resumeGrantNonce === 0 && !paymentReturn ? readCheckoutToken(assessmentId) : undefined;
    if (resumeGrantNonce > 0) clearCheckoutToken(assessmentId);
    if (existing) {
      setCheckoutGrant(existing);
      setResumeGrantPending(false);
      return;
    }
    let cancelled = false;
    setResumeGrantPending(true);
    mintCheckoutToken(assessmentId, clinicSlug)
      .then((minted) => {
        if (cancelled) return;
        writeCheckoutToken(assessmentId, minted.checkoutToken);
        setCheckoutGrant(minted.checkoutToken);
        /* THE PLAN-LINK LANE'S ONE SOURCE OF TRUTH. This mint is the only server
           round-trip `?resume=` makes before the plan step renders, so it is the
           only place the verdict can arrive on this journey. */
        setResumeGrantPending(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (resumeId && err instanceof ApiError && (err.status === 404 || err.status === 409)) {
          setResumeExpired(true);
        } else {
          setCheckoutEnterError('Sorry — we could not start checkout. Please try again.');
        }
        setResumeGrantPending(false);
      });
    return () => {
      cancelled = true;
    };
  }, [checkoutEntryId, paymentReturn, resumeId, assessmentId, clinicSlug, authStatus, isSignedIn, resumeGrantNonce]);

  /**
   * T-1 (2026-09-10) — TELL THE SERVER THE PATIENT OPENED THEIR PLAN LINK.
   *
   * The measurement the conversion review kept needing: of the patients a
   * clinician approves, how many ever SEE their recommendation? An approved
   * patient who never paid looks identical whether the email died or the price
   * put them off, and those have completely different fixes.
   *
   * ⚠️ DELIBERATELY NOT PART OF THE GRANT EFFECT ABOVE, though that is the
   * other server round-trip this lane makes and the obvious place to hang it.
   * Two reasons, and each one alone is enough:
   *
   *  · that effect RETURNS EARLY when the patient is signed in (a Bearer needs
   *    no guest grant), so a stamp inside it would miss every patient who
   *    already has an account — the renewal cohort, and the one most worth
   *    knowing about;
   *  · the same mint is called by `enterCheckout` on the ORDINARY self-serve
   *    journey, where nobody was ever sent a plan link. Stamping there would
   *    invent opens for patients who never received anything, which is worse
   *    than having no number at all.
   *
   * `resumeId` is the whole gate: it exists only when the address bar carried
   * a `#resume=` (or the deprecated `?resume=`), which is only ever a link a
   * clinician or an admin sent this patient.
   *
   * FIRED REGARDLESS OF WHETHER THE GRANT SUCCEEDS. A patient who opens an
   * expired link still opened it, and that is a fact worth having — arguably
   * the most interesting one on this screen.
   *
   * ONCE PER ASSESSMENT, via a ref rather than the effect's own deps: a
   * re-render, a grant retry or a StrictMode double-invoke must not turn one
   * open into several. The server is set-once anyway, so this is politeness
   * about traffic rather than correctness — but the throttle in front of the
   * route is real, and the patient's next request is the one that matters.
   */
  const planViewedBeaconFor = useRef<string | null>(null);
  useEffect(() => {
    if (!resumeId || !assessmentId) return;
    if (planViewedBeaconFor.current === assessmentId) return;
    planViewedBeaconFor.current = assessmentId;
    void recordPlanViewed(assessmentId).catch(() => {
      // Swallowed on purpose, and this is the one place it may be. The patient
      // asked to see their plan, not to be told a measurement failed — and
      // there is nothing they could do about it. See recordPlanViewed.
    });
  }, [resumeId, assessmentId]);

  // THE WEIGHT PHOTO IS REQUIRED OF EVERYONE BEFORE CHECKOUT (2026-08-30, CTO:
  // "they shouldn't be able to go beyond that point without doing that").
  //
  // There is no flag to derive any more — PlanSelection has no `photoRequired`
  // prop, because a prop is something one caller can pass over. The gate used
  // to fire only when clinic-service flagged the lane; everyone else got the ask
  // with an open Continue and a defer link beside it, and took it. Weeks of real
  // submissions later there were effectively no photos to review.
  //
  // The QUESTIONNAIRE's own photo question keeps its skip (CTO: "if we have the
  // option to skip, then that's fine, keep it") — the ask simply moves to a
  // point the patient cannot pass.

  /** Drop every hard-stop resume pointer so a freshly minted assessment
   * always starts at question 1 with an empty replay log. */
  const clearHardStopResumeState = useCallback(() => {
    setInitialQuestionKey(undefined);
    replayLogRef.current = [];
    submittedAnswerLogRef.current = [];
    setSeededAnswers(undefined);
    setLeadingCaptureSubmitted(false);
    setReplayHardStop(null);
  }, []);

  const startForm = useCallback(async () => {
    setStartError(null);
    // A fresh assessment must always begin at question 1 — never inherit a
    // stale resume pointer left over from an in-flow hard-stop "Back".
    clearHardStopResumeState();
    try {
      const created = await createAssessment({
        clinicSlug,
        ...(leadId ? { leadId } : {}),
        ...(attribution ? { attribution } : {}),
      });
      setAssessmentId(created.assessmentId);
      stampAssessment(created.assessmentId);
      setStage('questions');
    } catch {
      setStartError('Sorry — we could not start your check. Please try again.');
    }
  }, [clinicSlug, leadId, attribution, stampAssessment, clearHardStopResumeState]);

  /**
   * Graph Continue — eligibility path only (no RouteChoice). Since 2026-09-12
   * this opens the QUALIFICATION screen rather than the questionnaire; the
   * questionnaire is started from `startQuestionnaire` below, two screens on.
   */
  const continueFromProjection = useCallback(() => {
    setStartError(null);
    setStage('qualify');
  }, []);

  /**
   * The old Graph-Continue body, now triggered by "Start my questions" on the
   * What-happens-next screen. `startForm` is only called when there is no
   * assessment yet — the widget hand-off arrives with one already created.
   */
  const startQuestionnaire = useCallback(() => {
    if (assessmentId) {
      setStartError(null);
      setStage('questions');
      return;
    }
    void startForm();
  }, [assessmentId, startForm]);

  /**
   * The qualification screen's answer, kept here so coming BACK to the screen
   * re-selects it instead of silently discarding it. `null` means "not answered
   * on this journey yet" — the screen has not been reached, or the journey
   * entered past it (a restore, the plan link). It must never be turned into a
   * default. The screen itself is REQUIRED since 2026-09-25 (CTO), so once it
   * has been passed this always holds a real key; see QualificationStep.tsx.
   */
  const [readiness, setReadiness] = useState<LeadReadinessKey | null>(null);
  /**
   * ONE record per lead. The patient can reach this screen more than once
   * (questionnaire → Back → projection → forward), and while the server write
   * is fill-null-only and idempotent, a repeat POST is a repeat public write we
   * have no reason to make.
   */
  const recordedReadinessForLeadRef = useRef<string | null>(null);

  /**
   * The qualification screen's `onDone`. Since 2026-09-25 (CTO) the screen is
   * REQUIRED and the tap on an option IS the answer, so this always receives a
   * real key — there is no skip left to handle. It must move the journey on
   * synchronously: the screen hands its answer up at most once per mount and
   * relies on being replaced (QualificationStep.tsx).
   */
  const continueFromQualification = useCallback(
    (answer: LeadReadinessKey) => {
      setReadiness(answer);
      const lead = leadId;
      // NOTHING TO ATTACH IT TO is a normal outcome, not an error: a cold
      // `?assessment=` restore and a failed Step-0 lead POST both arrive here
      // with no leadId. The answer is simply not recorded, and the patient
      // moves on exactly as if it had been.
      if (lead && recordedReadinessForLeadRef.current !== lead) {
        recordedReadinessForLeadRef.current = lead;
        // Fire-and-forget. This is funnel plumbing — a segment for a sales
        // board — and it must never delay, block or fail the journey. Same
        // posture as the Step-0 lead POST it used to ride on.
        void recordLeadReadiness({ leadId: lead, intent: answer }).catch(() => {
          // Unknown lead, expired hand-off, offline, gateway not yet deployed —
          // all the same to us, and none of them is the patient's problem.
          recordedReadinessForLeadRef.current = null;
        });
      }
      setStage('whatNext');
    },
    [leadId],
  );

  const backFromProjection = useCallback(() => {
    if (projectionBackToCapture) {
      setStage('capture');
      return;
    }
    // Widget / assessment handoff: leave this SPA entry (typically back to Webflow).
    navigate(-1);
  }, [projectionBackToCapture, navigate]);

  // The building beat: the staged "We’re checking your answers" checklist over
  // the submit, then branch (CLIN-02). `runBuildingBeat` owns the pacing and
  // ties each step to real progress — step 2 ticks only once the submit has
  // ANSWERED, step 3 only once the best-effort measurement read below has
  // settled (journeyLoaders.ts) — and holds each narrated step long enough to
  // read (a 4.7s floor, CTO 2026-09-24); `buildView` is only what the screen
  // draws. The beat reports its first frame synchronously, so a retry always
  // opens on step 1, never on the failed attempt's leftovers.
  //
  // ⚠️ THE FLOOR PACES THE SCREEN, NOTHING ELSE (review, 2026-09-25). The
  // marketing grant and `eligibility_result` hang off the SUBMIT's own answer,
  // not off the beat: chained after the 4.7s floor they fired ~3s later than
  // the server allowed, and a patient who closed the tab on "All done" lost
  // the funnel's "assessment completed" step and the "Keeping in touch" tick
  // they had made. The verdict's SCREEN still waits for the beat — the Stop
  // screen as much as "Good news" (CLIN-02: the checklist is neutral for both
  // outcomes, so its timing must not announce the verdict either).
  const [submitError, setSubmitError] = useState<string | null>(null);
  /**
   * The assessment THIS document submitted and got an eligible verdict for —
   * the one checkout lane whose "Getting your order ready" loader carries the
   * checkout journey card and its set loading time (CTO 2026-09-25,
   * `checkout/checkoutLoaderContent.ts`): opened from this Result screen, the
   * self-serve hold route, no plan chosen yet. A cold restore, a plan link, a
   * 3D Secure return or the dashboard never set it — their checkout may be the
   * clinician-led route or land past the plan screen, where the card's
   * "Choose your treatment & plan" and "Card held, not charged" are not true.
   */
  const [submittedHereId, setSubmittedHereId] = useState<string | null>(null);
  const [buildView, setBuildView] = useState<BuildingBeatView>(BUILDING_BEAT_START);
  const heightCmRef = useRef(heightCm);
  const weightKgRef = useRef(weightKg);
  const leadIdRef = useRef(leadId);
  const marketingTicksRef = useRef(marketingTicks);
  heightCmRef.current = heightCm;
  weightKgRef.current = weightKg;
  leadIdRef.current = leadId;
  marketingTicksRef.current = marketingTicks;
  const runSubmit = useCallback(() => {
    if (!assessmentId) return;
    setSubmitError(null);
    setStage('building');
    const submitted = submitAssessment(assessmentId, clinicSlug);

    // ── On the SUBMIT's answer: the two fire-and-forget writes (see above) ──
    // Their payloads never depended on the loader, and both are the first
    // moment the server will take them. A throw here (a GTM tag) is logged and
    // can never reach the verdict chain below: it is a separate chain.
    submitted
      .then(
        () => {
          // THE MARKETING GRANT (one-flow, 2026-09-23): the "Keeping in touch"
          // tick made on the consent screen is recorded HERE — the first moment
          // the server will accept it (it 404s any pre-submit write). Fired for
          // every verdict and NEVER awaited: `postJourneyMarketingGrant` sends
          // nothing when nothing was ticked, never rejects, settles within its
          // 4s budget, and swallows every refusal — a `not_eligible` verdict's
          // 404 is the server correctly declining to hold marketing consent for
          // a stopped journey (parity with the old Result-screen card, which
          // only the eligible screen ever mounted). Whatever it does, the
          // verdict's screen never waits on it. WhatsApp rides only when the
          // block reported its box as SHOWN — a tick on a box the patient never
          // saw is never sent.
          void postJourneyMarketingGrant(assessmentId, clinicSlug, marketingTicksRef.current, {
            whatsapp: marketingWhatsappShownRef.current,
          });
          // C4 — eligibility_result: the lead_id and NOTHING about the verdict
          // (Part F / Art. 9). `result.eligibility` deliberately never crosses
          // into tracking — the helper has no parameter for it (and this
          // handler never even reads the result), so the same payload goes out
          // for eligible, clinician_review and not_eligible. Fire for every
          // verdict: this is the funnel's "assessment completed" step, so
          // skipping the unhappy paths would skew the denominator.
          if (leadIdRef.current) {
            trackEligibilityResult({ leadId: leadIdRef.current });
          }
        },
        // A refused submit: the verdict chain below owns the patient-facing
        // error; there is nothing to record here.
        () => undefined,
      )
      .catch((err: unknown) => {
        console.error('[journey] post-submit step failed', err);
      });

    // ── "Preparing your results": the best-effort measurement read ──
    // A journey CAN land here with height/weight still at 0 — progress is
    // deliberately PHI-minimal and never carries them, so a cold
    // `?assessment=` restore only gets them back if the question set leads
    // with a `capture` question for the patient to re-answer (QuestionFlow
    // pushes that back up through `onCaptureChange`). Where we also hold a
    // leadId, the lead is a cheap way to recover them.
    //
    // It runs INSIDE the checklist's step 3 (`runBuildingBeat`'s `prepare`),
    // so "Preparing your results" stays current until it has settled — the
    // list never says "All done" over a read still in flight (review,
    // 2026-09-25). The answer is held here and applied with the stage change
    // below, in the same render as before.
    //
    // BEST-EFFORT, AND EXPLICITLY SO. This read is an ENRICHMENT of a result
    // the patient has already earned: the assessment is submitted and
    // immutable by the time it runs, so its failure must never reach the
    // patient as "we could not submit your answers" — the retry that error
    // offers 409s, and it would be shown for a SUCCESSFUL submission. It once
    // did exactly that, because an unguarded `await` inside the success
    // handler rejected the whole chain into the submit-failure handler (which
    // also swallowed the C4 push). `runBuildingBeat` swallows a `prepare`
    // rejection by contract, and the try/catch below says so again here.
    // clinic-service `fix/lead-resume-capability-ttl` (#149) turns that from a
    // rare fault into the expected answer: the hand-off is now single-use with
    // a 15-minute TTL from `Lead.createdAt`, sized for the ONE browser redirect
    // it was written for, and this call site runs at the END of the
    // questionnaire — comfortably outside both windows, and past the
    // single-use consume in any case.
    //
    // WHY THE LEAD AND NOT THE TIER-2 READ, given the capability is now
    // deliberately short-lived. The Tier-2 sources (`result-summary`,
    // `account-bootstrap`) are authoritative and outlive the lead, but they
    // are token-gated, and in-flow we hold no token — Option C mints one from
    // an EMAILED CODE, and putting a code gate between a patient and the
    // result they just finished answering for would be a far worse outcome
    // than a result screen without a projection chart. Minting a checkout
    // grant early to borrow its token would move a checkout-scoped capability
    // into the questionnaire lane for a cosmetic enrichment. So the lead
    // stays: it is the only source reachable here without a gate, it is free
    // when it works, and it now costs nothing when it does not. If #149 later
    // drops the hand-off's lifetime to something this can never fall inside,
    // delete the read rather than widen it — the result screen already
    // renders correctly without the answer.
    let recovered: { heightCm: number; weightKg: number } | null = null;
    const prepareResult = async (result: Awaited<typeof submitted>) => {
      if (result.eligibility === 'not_eligible') return;
      if (!(heightCmRef.current > 0 && weightKgRef.current > 0) && leadIdRef.current) {
        try {
          const lead = await resumeLead(leadIdRef.current);
          recovered = { heightCm: lead.heightCm, weightKg: lead.weightKg };
        } catch {
          // Expired / spent / purged / offline — all the same to us. The
          // ResultScreen renders the verdict and the CTA without them and
          // simply omits the two blocks that need real measurements; it
          // never paints a 0 kg projection or a NaN dial.
        }
      }
    };

    // Rejects the moment the submit does (the Promise.all it replaced did the
    // same), so the two-argument `then` below still binds the retryable error
    // to the SUBMIT alone. `undefined` keeps the production floor (the default
    // — a spec swaps it through `testing/fastJourneyBeats.ts`).
    runBuildingBeat(submitted, setBuildView, undefined, prepareResult)
      .then(
        (result) => {
          setSubmitResult(result);
          // CLIN-02 — not_eligible must render the StopScreen, never "good news".
          if (result.eligibility === 'not_eligible') {
            setStage('stopped');
          } else {
            if (recovered) {
              setHeightCm(recovered.heightCm);
              setWeightKg(recovered.weightKg);
            }
            setSubmittedHereId(assessmentId);
            setStage('result');
          }
        },
        // TWO-ARGUMENT `then`, NOT `.then(...).catch(...)` — the patient-facing
        // submit error is bound to the SUBMIT rejecting, and to nothing else. The
        // trailing `.catch` below is downstream of this handler, so it cannot see
        // this rejection; and this handler is upstream of the success one, so it
        // cannot see anything the success handler throws. That is the whole point:
        // the previous shape gave one `.catch` both jobs, which is how a failed
        // enrichment read (and, before it, a GTM tag that threw) ended up telling
        // a patient their answers had not been submitted when they had.
        () => {
          setSubmitError('Sorry — we could not submit your answers. Please try again.');
        },
      )
      // Anything that goes wrong AFTER a successful submit: log it, never paint
      // it. Without this the success handler's rejection would be an unhandled
      // one, which is silent in the browser and noisy in the test runner.
      .catch((err: unknown) => {
        console.error('[journey] post-submit step failed', err);
      });
  }, [assessmentId, clinicSlug]);

  // NOTE: there is deliberately NO effect watching for the 'building' stage.
  // `runSubmit` is the ONLY thing that sets 'building' (since #120, a restore
  // resumes the questionnaire and can never land here), so a watcher would just
  // re-fire runSubmit after the in-flow onComplete already called it — a double
  // POST whose loser 409s ("immutable once submitted") and flashes the error
  // state while the winner sits out the building beat. One trigger, one submit.

  function handleCaptured(result: CaptureResult) {
    setJourneyFirstName(result.firstName ?? null);
    setHeightCm(result.heightCm);
    setWeightKg(result.weightKg);
    setLeadId(result.leadId);
    setJourneyEmail(result.email);
    setProjectionBackToCapture(true);
    setStage('projection');
  }

  function restart() {
    setSubmitResult(null);
    setJourneyFirstName(null);
    setAssessmentId(undefined);
    setResumeExpired(false);
    // A restart mints a NEW lead on the next capture, so the readiness answer
    // and its one-write memo belong to a row this journey no longer has.
    setReadiness(null);
    recordedReadinessForLeadRef.current = null;
    // And a fresh journey starts from the lawful UNTICKED default — a tick
    // from the run that just ended must never arrive pre-made on a consent
    // screen this journey has not reached yet.
    setMarketingTicks(UNTICKED);
    marketingWhatsappShownRef.current = false;
    // Same stale-pointer clear as startForm / backToProjectionAfterStop —
    // a full restart must never land mid-flow on the next assessment.
    clearHardStopResumeState();
    setStage('capture');
  }

  /**
   * Fallback for the post-submit StopScreen's "Back" (CLIN-02 `not_eligible`
   * from the FINAL submit) when there is no answer log to replay (e.g. the
   * questionnaire had zero interactive questions). Land on Projection (same
   * screen Continue already uses to start a fresh assessment) with the stale
   * assessmentId cleared, so Continue calls `startForm` for a brand-new draft
   * instead of touching the immutable one. The common case instead goes
   * through `resumeToLastAnsweredQuestion` below.
   */
  const backToProjectionAfterStop = useCallback(() => {
    setSubmitResult(null);
    setResumeExpired(false);
    setAssessmentId(undefined);
    // Clear any resume pointer from an earlier in-flow hard-stop "Back" —
    // the next Continue mints a brand-new assessment that must start at
    // question 1, not mid-flow.
    clearHardStopResumeState();
    setStage('projection');
  }, [clearHardStopResumeState]);

  // Re-entrancy guard for `resumeAfterHardStop` below — the StopScreen stays
  // mounted (and its Back button clickable) for the duration of the replay,
  // so a double-click must not mint two fresh assessments.
  const resumingAfterStopRef = useRef(false);

  /**
   * The IN-FLOW hard-stop "Back" (before the final submit) — QuestionFlow's
   * `onRestart`. CLIN-03 makes the stopped assessment permanently immutable,
   * so there is no literal resume, but the patient's intent ("let me pick a
   * different answer to that last question") is still honoured: mint a
   * brand-new assessment, post the leading capture FIRST (so BMI-conditional
   * rules see height/weight), replay every interactive answer that came
   * BEFORE the stop (honouring each verdict — a mid-replay hard_stop shows
   * StopScreen immediately), then re-enter QUESTIONS at the server's
   * `nextQuestionKey` from the last successful replay (falling back to
   * `stopQuestionKey`). Height/weight/email are already known, so Capture
   * and Projection are both skipped entirely.
   *
   * A capture-kind stop (BMI floor — no `context`) has no "different option"
   * to redo, so it falls back to the safe Projection restart, same as the
   * post-submit CLIN-02 stop below.
   */
  const resumeAfterHardStop = useCallback(
    async (context?: HardStopRestartContext) => {
      if (!context || resumingAfterStopRef.current) {
        if (!context) backToProjectionAfterStop();
        return;
      }
      resumingAfterStopRef.current = true;
      setStartError(null);
      setReplayHardStop(null);
      setHardStopResuming(true);
      // Clear the (stopped) assessmentId FIRST so QuestionFlow actually
      // unmounts — its `key={assessmentId}` only forces a remount on a
      // CHANGE, and a freshly-minted assessment is not guaranteed to differ
      // from a stale one still in view (and must not visibly reuse its old,
      // now-invalid `stopState`/answers either way).
      setAssessmentId(undefined);
      // Carry the parent's replay log forward AND merge anything QuestionFlow
      // handed us — a remounted flow starts with an empty answerLogRef, so
      // without this a second consecutive hard stop would replay nothing.
      const priorAnswers = mergeAnswerLogs(replayLogRef.current, context.priorAnswers);
      try {
        const created = await createAssessment({
          clinicSlug,
          ...(leadId ? { leadId } : {}),
          ...(attribution ? { attribution } : {}),
        });

        // Capture FIRST — the clinical rules engine needs height/weight on
        // the record before any BMI-conditional answer is evaluated.
        const h = heightCmRef.current;
        const w = weightKgRef.current;
        let captureApplied = false;
        if (h > 0 && w > 0) {
          const set = await getQuestions(created.assessmentId);
          const sorted = [...set.questions].sort((a, b) => a.idx - b.idx);
          for (const question of sorted) {
            if (question.kind !== 'capture') break;
            const captureResult = await submitAnswer(created.assessmentId, clinicSlug, {
              questionKey: question.key,
              value: { heightCm: h, weightKg: w },
            });
            captureApplied = true;
            if (captureResult.outcome === 'hard_stop') {
              // BMI-floor / capture stop — no selectable "different option".
              replayLogRef.current = priorAnswers;
              setSeededAnswers(priorAnswers);
              setLeadingCaptureSubmitted(true);
              setSubmitResult(null);
              setResumeExpired(false);
              setInitialQuestionKey(undefined);
              setAssessmentId(created.assessmentId);
              stampAssessment(created.assessmentId);
              setReplayHardStop({ result: captureResult });
              setStage('questions');
              return;
            }
          }
        }

        // Server-authoritative landing key: last successful nextQuestionKey
        // wins over the client-hard-coded stopQuestionKey, so an incomplete
        // replay lands on the first unanswered question rather than jumping
        // past it.
        let landingKey: string = context.stopQuestionKey;
        for (let i = 0; i < priorAnswers.length; i += 1) {
          const entry = priorAnswers[i]!;
          const result = await submitAnswer(created.assessmentId, clinicSlug, {
            questionKey: entry.questionKey,
            value: entry.value,
            ...(entry.detail ? { detail: entry.detail } : {}),
          });
          if (result.outcome === 'hard_stop') {
            const restart: HardStopRestartContext = {
              stopQuestionKey: entry.questionKey,
              priorAnswers: priorAnswers.slice(0, i),
            };
            replayLogRef.current = restart.priorAnswers;
            setSeededAnswers(restart.priorAnswers);
            setLeadingCaptureSubmitted(captureApplied);
            setSubmitResult(null);
            setResumeExpired(false);
            setInitialQuestionKey(undefined);
            setAssessmentId(created.assessmentId);
            stampAssessment(created.assessmentId);
            setReplayHardStop({ result, restart });
            setStage('questions');
            return;
          }
          if (typeof result.nextQuestionKey === 'string') {
            landingKey = result.nextQuestionKey;
          }
        }

        replayLogRef.current = priorAnswers;
        setSeededAnswers(priorAnswers);
        setLeadingCaptureSubmitted(captureApplied);
        setSubmitResult(null);
        setResumeExpired(false);
        setInitialQuestionKey(landingKey);
        setAssessmentId(created.assessmentId);
        stampAssessment(created.assessmentId);
        setStage('questions');
      } catch (err) {
        // Replay failed (network blip / create 5xx / answer 409) — fall back
        // to the always-safe Projection restart rather than stranding the
        // patient on a half-built assessment. Log so create vs answer failure
        // is diagnosable (the UI fallback itself stays intentional).
        console.error('[journey] hard-stop resume failed', err);
        clearHardStopResumeState();
        backToProjectionAfterStop();
      } finally {
        resumingAfterStopRef.current = false;
        setHardStopResuming(false);
      }
    },
    [clinicSlug, leadId, attribution, stampAssessment, backToProjectionAfterStop, clearHardStopResumeState],
  );

  /**
   * The post-submit StopScreen's "Back" (CLIN-02 `not_eligible` from the
   * FINAL submit). The just-submitted assessment is immutable (answers 409
   * once `state: 'submitted'`), so this can never resume THAT assessment —
   * but every answer that led to it is still known (`submittedAnswerLogRef`,
   * captured off QuestionFlow's `onComplete`). Treat the last answered
   * question as the "stop point" and hand off to `resumeAfterHardStop`,
   * which mints a fresh assessment, replays every answer BEFORE it, and
   * lands the patient back on that last question free to pick a different
   * answer — the same mechanics the in-flow hard-stop "Back" already uses.
   * Falls back to Projection when there is no answer to replay.
   */
  const resumeToLastAnsweredQuestion = useCallback(() => {
    const log = submittedAnswerLogRef.current;
    const last = log[log.length - 1];
    if (!last) {
      backToProjectionAfterStop();
      return;
    }
    void resumeAfterHardStop({ stopQuestionKey: last.questionKey, priorAnswers: log.slice(0, -1) });
  }, [backToProjectionAfterStop, resumeAfterHardStop]);

  const entryLoading = clinicState === 'loading' || leadPending || restoring || hardStopResuming || resumeGrantPending;
  // ONCE DRAWN, A SHORT MINIMUM (review, 2026-09-25): a read landing just
  // after the loader's 300ms show delay used to cut its mark off part-way
  // through the fade-in. `useLoaderHold` keeps it up until the mark has been
  // drawn for 500ms (the loader reports the draw through `onShown`) — only
  // when it WAS drawn, so a quick read is never held.
  // …BUT NEVER OVER AN ERROR (release review, 2026-09-25 — REQUIREMENTS A8): a
  // clinic read or a restore that FAILED goes straight to its screen below, not
  // after up to 500ms more of "Opening your health check".
  const entryWaitFailed = clinicState === 'error' || clinicState === 'missing' || restoreFailed;
  const [showEntryLoader, entryMarkShown] = useLoaderHold(entryLoading, entryWaitFailed);
  // The lane the loader named while it waited. During the hold every flag is
  // down, and read afresh they would swap "Restoring your progress" (or
  // "Checking your link") for "Opening your health check" in its last half
  // second — a line that is no longer true, announced by the status region.
  const paymentReturning = paymentReturn !== null;
  const entryLoaderFlagsRef = useRef({
    hardStopResuming,
    resumeGrantPending,
    leadPending,
    restoring,
    paymentReturn: paymentReturning,
  });
  if (entryLoading) {
    entryLoaderFlagsRef.current = { hardStopResuming, resumeGrantPending, leadPending, restoring, paymentReturn: paymentReturning };
  }

  if (showEntryLoader) {
    // Wave 6 §1: journey entry (and the widget/resume/restore hand-offs) get
    // the BRANDED loading moment, not a bare spinner — this is the seam
    // between marketing and care. Restoring NEVER flashes capture (or any
    // other stage) first.
    return (
      <PageFrame narrow className="gps-journey-root">
        {/* The journey look (2026-09-25): the brand mark in its ring, a show
            delay so a quick read never flashes, and — on a fresh start only —
            a small card that shows the journey while it loads (CTO: "a bit of
            a small content page … load one after the other, showing the
            journey till the main page loads … make the card grow … use more
            icons"): four icon-led steps and three chips revealed one after
            another, the card growing as they arrive, then static — every line
            a claim this journey already makes (entryLoaderContent.ts). It has
            no control, so it can never hold focus when the read lands. */}
        <BrandedLoader
          look="journey"
          message={journeyLoaderMessage(entryLoaderFlagsRef.current)}
          card={journeyLoaderCard(entryLoaderFlagsRef.current)}
          onShown={entryMarkShown}
        />
      </PageFrame>
    );
  }

  // Option C — a return visit resolved to a Tier-2 read (the ResultScreen
  // hydrate). Prove control of the assessment email with the 6-digit code
  // before the health data loads; the verified token is spent immediately by
  // hydrateResultFromToken. Only reached on a cold `?assessment=` restore.
  if (restoreCodePurpose === 'result' && assessmentIdFromUrl) {
    return (
      <PageFrame narrow className="gps-journey-root">
        <RestoreCodeEntry assessmentId={assessmentIdFromUrl} onVerified={hydrateResultFromToken} />
      </PageFrame>
    );
  }

  // Case A — progress read failed transiently. Assessment identity stays in
  // the URL and in state; Retry re-calls GET …/progress. Never CaptureStep.
  if (restoreFailed && assessmentIdFromUrl) {
    return (
      <PageFrame narrow className="gps-journey-root">
        <ErrorState
          message="Sorry — we could not restore your progress. Please try again."
          onRetry={runRestore}
        />
      </PageFrame>
    );
  }

  if (clinicState === 'error') {
    return (
      <PageFrame narrow className="gps-journey-root">
        <ErrorState message="Sorry — we could not load this clinic." onRetry={loadClinic} />
      </PageFrame>
    );
  }

  if (clinicState === 'missing') {
    return (
      <PageFrame narrow className="gps-journey-root">
        <Title>We can&rsquo;t find that clinic</Title>
        <Help>
          The page you followed doesn&rsquo;t match one of our clinics.{' '}
          {/* A raw anchor (full document load), so it carries the served prefix
              itself — a <Link> would, this cannot (config.ts BASENAME). */}
          <a href={withBasename('/weightloss')} style={{ color: 'var(--gps-link)' }}>
            Go to our weight-management clinic
          </a>
          .
        </Help>
      </PageFrame>
    );
  }

  if (clinic && clinic.status !== 'live') {
    return (
      <PageFrame narrow className="gps-journey-root">
        <Title>{clinic.name}</Title>
        <Help>This clinic is coming soon. Check back shortly — we&rsquo;re nearly ready.</Help>
      </PageFrame>
    );
  }

  const clinicName = clinic?.name ?? 'Our clinic';

  // The resumed assessment is gone (bad/expired id, not submitted) — a
  // friendly restart into the normal journey, never a dead end.
  if (resumeExpired) {
    return (
      <PageFrame narrow className="gps-journey-root">
        <Title>This link has expired</Title>
        <Help>
          The plan link you followed is no longer valid — but it only takes a couple of minutes to
          check again.
        </Help>
        <Button type="button" variant="cta" onClick={restart}>
          Start your 2-minute check
        </Button>
      </PageFrame>
    );
  }

  // THE IN-JOURNEY CALL-BACK OFFER (CTO, 2026-09-11 — "anytime"). ONE element,
  // built here because the lead, the attribution and the `requested` flag live
  // here. Two screens place it inside their own column — QuestionFlow (its
  // question screens, never its stop screen) and ResultScreen (above its fixed
  // bar) — and the stages without a layout of their own get it underneath.
  // NEVER beside a stop screen, which carries the call-back form inline, and
  // never while the answers are being submitted — nor while Step 0's capture
  // loader is saving the details (`captureLoaderShown`).
  const callbackOffer = (
    <JourneyCallbackOffer
      clinicSlug={clinicSlug}
      attribution={attribution}
      leadId={leadId}
      requested={callbackRequested}
      onRequested={() => setCallbackRequested(true)}
    />
  );
  const offerBelowStage =
    !replayHardStop &&
    ((stage === 'capture' && !captureLoaderShown) || stage === 'projection' || stage === 'checkout');

  // Narrow PageFrame for every journey stage (RouteChoice wide frame is unused
  // on the Weight Loss path — Webflow owns eligibility vs callback) EXCEPT the
  // result: its side-by-side layout (2026-09-23, CTO — no scrolling to see the
  // verdict and the projection) needs the standard frame, and JourneyScreen's
  // wide variant caps the content at 64rem inside it. No `.gps-plan-breakout`
  // here, ever: its translateX would break the fixed "See my plan" bar (see
  // the FadeIn note below).
  const stageContent = (
    <>
      {startError ? (
        <div role="alert" style={{ color: 'var(--gps-danger-text)', fontSize: '13.5px', marginBottom: 'var(--gps-s-3)' }}>
          {startError}
        </div>
      ) : null}

      {/* A light fade+rise as the journey moves between stages (keyed by stage).
          The wrapper's settled transform is `none` (see FadeIn) so ResultScreen's
          fixed CTA bar keeps anchoring to the viewport. Reduced-motion → instant (E3 #3). */}
      <FadeIn resetKey={stage}>
      {stage === 'capture' ? (
        <CaptureStep
          clinicSlug={clinicSlug}
          clinicName={clinicName}
          attribution={attribution}
          variant={variant}
          onDone={handleCaptured}
          onLoaderShown={setCaptureLoaderShown}
        />
      ) : null}

      {stage === 'projection' ? (
        <ProjectionInterstitial
          weightKg={weightKg}
          percent={8}
          onBack={backFromProjection}
          onContinue={continueFromProjection}
        />
      ) : null}

      {/* The qualification form and the explainer, in the CTO's order, at the
          only position where every acquisition lane sees them. Switch & Save is
          NOT between them: its panel stays beside the questionnaire's
          prior-treatment question, and that QUESTION stays behind the
          eligibility gate (its options name prescription-only medicines). The
          saving SENTENCE is no longer post-gate only — since 2026-09-25 (CTO)
          the entry loader's content card shows it too, before any gate
          (entryLoaderContent.ts; an open CTO item on its evidence, see
          `WhatHappensNext.tsx`). */}
      {stage === 'qualify' ? (
        <QualificationStep
          percent={9}
          initialReadiness={readiness}
          onBack={() => setStage('projection')}
          onDone={continueFromQualification}
        />
      ) : null}

      {stage === 'whatNext' ? (
        <WhatHappensNext percent={10} onBack={() => setStage('qualify')} onContinue={startQuestionnaire} />
      ) : null}

      {replayHardStop ? (
        <StopScreen
          result={replayHardStop.result}
          clinicSlug={clinicSlug}
          attribution={attribution}
          leadId={leadId}
          onRestart={() => {
            const ctx = replayHardStop.restart;
            setReplayHardStop(null);
            void resumeAfterHardStop(ctx);
          }}
          restartLabel="Back"
        />
      ) : null}

      {stage === 'questions' && assessmentId && !replayHardStop ? (
        <QuestionFlow
          key={assessmentId}
          assessmentId={assessmentId}
          clinicSlug={clinicSlug}
          attribution={attribution}
          capturePrefill={{ heightCm, weightKg }}
          leadId={leadId}
          callbackOffer={callbackOffer}
          // The "Keeping in touch" ticks for the consent screen — state lives
          // above so it survives this flow remounting; recorded by runSubmit.
          marketing={{
            choices: marketingTicks,
            onToggle: toggleMarketingTick,
            onWhatsappShownChange: reportMarketingWhatsappShown,
          }}
          initialQuestionKey={initialQuestionKey}
          seededAnswers={seededAnswers}
          leadingCaptureSubmitted={leadingCaptureSubmitted}
          onCaptureChange={(h, w) => {
            setHeightCm(h);
            setWeightKg(w);
          }}
          onDobChange={setJourneyDob}
          onComplete={(answerLog) => {
            submittedAnswerLogRef.current = answerLog;
            runSubmit();
          }}
          // CLIN-03: a hard stop is STICKY the instant it fires (the
          // clinic-service durably marks `stoppedAt` and 409s any further
          // answer against this assessment, even the exact question that
          // triggered it) — so "Back" can NEVER resume this assessment.
          // `resumeAfterHardStop` mints a fresh one, replays everything
          // answered before the stop, and lands back on that exact question.
          onRestart={resumeAfterHardStop}
          // Back off the FIRST question returns to the screen immediately
          // before it — What-happens-next since 2026-09-12, Projection before
          // that. RouteChoice is no longer on the canonical path. Answers
          // already saved stay. (Without a real capture there is nothing to go
          // back TO but Step 0: the projection chart cannot be drawn from a
          // zero weight, and the two new screens would strand the patient one
          // Continue away from a projection that cannot render.)
          onExit={() => setStage(heightCm > 0 && weightKg > 0 ? 'whatNext' : 'capture')}
        />
      ) : null}

      {stage === 'building' ? (
        submitError ? (
          <ErrorState message={submitError} onRetry={runSubmit} />
        ) : (
          <BuildingInterstitial phase={buildView.phase} stillWorking={buildView.stillWorking} />
        )
      ) : null}

      {/* A SUBMITTED ASSESSMENT ALWAYS GETS A SCREEN. This used to also require
          `heightCm > 0 && weightKg > 0`, which left the patient on a blank page
          whenever the measurements could not be recovered — the very cases the
          re-source above exists for, plus every cold restore on a question set
          with no leading `capture` question to re-ask them. The guard was right
          that nothing may be painted from a 0; it belongs around the two blocks
          that need one, so it now lives inside ResultScreen (which omits them
          and renders the verdict + CTA regardless). `submitResult` stays here
          because without a verdict there is genuinely nothing to say. */}
      {stage === 'result' && submitResult ? (
        <ResultScreen
          heightCm={heightCm}
          weightKg={weightKg}
          result={submitResult}
          callbackOffer={callbackOffer}
          firstName={journeyFirstName}
          onSeePlan={() => {
            void enterCheckout();
          }}
        />
      ) : null}

      {stage === 'stopped' ? (
        <StopScreen
          result={submitResult ? notEligibleResult(submitResult.flags.includes('mh_risk')) : notEligibleResult()}
          clinicSlug={clinicSlug}
          attribution={attribution}
          leadId={leadId}
          onRestart={resumeToLastAnsweredQuestion}
          restartLabel="Back"
        />
      ) : null}

      {stage === 'checkout' && assessmentId && (isSignedIn || checkoutGrant || reEntryToken) ? (
        <CheckoutFlow
          assessmentId={assessmentId}
          clinicSlug={clinicSlug}
          leadId={leadId}
          journeyEmail={journeyEmail}
          journeyDob={journeyDob}
          // The plan builder states its projection as a PERCENTAGE of body
          // weight; this is what turns it into this patient's own kilograms.
          // `weightKg` is 0 until the capture step has run (or been re-hydrated
          // on a resume), and 0 is passed as undefined rather than as a weight —
          // otherwise the band would render a confident "0kg" projection.
          startKg={weightKg > 0 ? weightKg : undefined}
          // The SAME figure the ResultScreen's dial showed one screen earlier —
          // `bmiOf` is the portal's ONE derivation. Sourcing it any other way
          // here would print two different BMIs on consecutive screens, which is
          // a worse failure than the one a second source is meant to avoid.
          bmi={heightCm > 0 && weightKg > 0 ? bmiOf(heightCm, weightKg) : undefined}
          reEntryToken={reEntryToken}
          checkoutToken={checkoutGrant ?? reEntryToken}
          // Whether the plan step MANDATES the weight photo. The journey is the
          // only place that holds the verdict this turns on — see the
          // Only a RESUMED assessment can be stale — the in-journey one was
          // just submitted, so its checkout failures keep the normal retry UI.
          onAssessmentGone={resumeId && assessmentId === resumeId ? () => setResumeExpired(true) : undefined}
          // T-2: the SAME predicate as above — only a patient who arrived on a
          // plan link is the approved-unpaid patient the question is for.
          offerDropOffReason={Boolean(resumeId && assessmentId === resumeId)}
          // Back from the bank's 3D Secure page: what Stripe says the bank
          // decided, for the checkout that return named (and no other).
          paymentReturn={
            pendingPaymentReturn && paymentReturn && assessmentId === paymentReturn.assessmentId
              ? pendingPaymentReturn
              : undefined
          }
          onPaymentReturnSettled={settlePaymentReturn}
          // The pre-plan lane: this document submitted THIS assessment and the
          // patient came here from its Result screen (see `submittedHereId`).
          preplanLoaderContent={submittedHereId !== null && submittedHereId === assessmentId}
        />
      ) : null}
      {checkoutEnterError ? (
        <ErrorState
          message={checkoutEnterError}
          onRetry={() => {
            // A GRANT whose exchange failed transiently: re-run the EXCHANGE,
            // not the checkout-token mint — there is no assessment id to mint
            // against yet, so the branches below would quietly do nothing.
            if (resumeGrant && !exchangedResumeId) {
              setCheckoutEnterError(null);
              setResumeExchangeNonce((n) => n + 1);
              return;
            }
            if (checkoutEntryId && !isSignedIn) {
              setCheckoutEnterError(null);
              setResumeGrantPending(true);
              setResumeGrantNonce((n) => n + 1);
              return;
            }
            void enterCheckout();
          }}
        />
      ) : null}
      </FadeIn>
      {/* Below the stages that have no column of their own to put it in (see
          `callbackOffer` above). Outside FadeIn so it does not flash on every
          stage change: it is the same offer, still there. */}
      {offerBelowStage ? callbackOffer : null}
    </>
  );

  // ONE call-back provider around the whole stage (2026-09-25), so Step 0's
  // "Leave this step?" retention dialog can hand off to the offer rendered
  // under that stage (LeaveConfirmDialog, journey variant). It is always
  // mounted, so no stage change swaps the tree. QuestionFlow still mounts its
  // own, nearer provider around each question screen, which is the one "Why we
  // ask" and the question screens' leave dialog read. Nothing else reads this
  // one: the projection, the result and checkout have no consumer.
  return (
    <PageFrame narrow={stage !== 'result'} className="gps-journey-root">
      <JourneyCallbackProvider>{stageContent}</JourneyCallbackProvider>
    </PageFrame>
  );
}
